import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { listCareersAdmin, reorderCareers, deleteCareer } from "@/lib/api/careers.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/careers/")({
  loader: () => listCareersAdmin(),
  component: AdminCareersList,
});

type Row = Awaited<ReturnType<typeof listCareersAdmin>>[number];

function AdminCareersList() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderCareers({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteCareer({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Role deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    { accessorKey: "location", header: "Location", meta: { filterVariant: "text" } },
    {
      accessorKey: "employmentType",
      header: "Type",
      meta: { filterVariant: "select", groupable: true },
    },
    {
      accessorKey: "department",
      header: "Department",
      meta: { filterVariant: "select", groupable: true },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", groupable: true },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/careers/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.title}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Careers"
        action={
          <Button asChild>
            <Link to="/admin/careers/new">
              <Plus className="mr-2 h-4 w-4" /> New role
            </Link>
          </Button>
        }
      />
      <DataTable
        tableId="careers"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
